import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { PageHero } from "./About";
import { LeadForm } from "../components/LeadForm";
import { COMPANY } from "../data/site";

const Contact = () => {
    return (
        <>
            <PageHero
                eyebrow="Get in Touch"
                title="Talk to our fire safety engineers."
                subtitle="Whether it's a site inspection, design review, AMC, or a bulk product enquiry — we respond to industrial leads within 24 hours."
            />

            <section className="section" data-testid="contact-section">
                <div className="container-vil grid lg:grid-cols-[1fr_1.2fr] gap-10">
                    {/* Contact info */}
                    <div className="space-y-4">
                        <InfoCard
                            icon={<Phone className="w-5 h-5 text-[#E53935]" />}
                            title="Call us"
                        >
                            <div className="space-y-3">
                                {COMPANY.contacts.map((contact) => (
                                    <div
                                        key={contact.phoneRaw}
                                        className="flex items-center justify-between gap-4 text-sm text-slate-700"
                                    >
                                        <span>{contact.name || contact.role || "Phone"}</span>
                                        <a
                                            href={`tel:${contact.phoneRaw}`}
                                            className="font-mono text-slate-900 hover:text-[#E53935]"
                                            data-testid={`contact-phone-${(contact.name || contact.role || "phone").toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            {contact.phone}
                                        </a>
                                    </div>
                                ))}
                            </div>
                            <div className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-1">
                                24 × 7 Site Support
                            </div>
                        </InfoCard>

                        <InfoCard
                            icon={<Mail className="w-5 h-5 text-[#E53935]" />}
                            title="Email us"
                        >
                            <div className="space-y-2">
                                {COMPANY.emails.map((e) => (
                                    <a
                                        key={e}
                                        href={`mailto:${e}`}
                                        className="block text-slate-700 hover:text-slate-900 transition text-sm"
                                        data-testid={`contact-email-${e.split("@")[0]}`}
                                    >
                                        {e}
                                    </a>
                                ))}
                            </div>
                        </InfoCard>

                        <InfoCard
                            icon={<MapPin className="w-5 h-5 text-[#E53935]" />}
                            title="Office address"
                        >
                            <p className="text-slate-700 text-sm leading-relaxed">{COMPANY.address}</p>
                        </InfoCard>

                        <InfoCard
                            icon={<Clock className="w-5 h-5 text-[#E53935]" />}
                            title="Working hours"
                        >
                            <div className="text-slate-700 text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span>Mon — Sat</span>
                                    <span className="font-mono">9:30 — 19:00</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Sunday</span>
                                    <span className="font-mono text-slate-500">By appointment</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-slate-100 mt-2">
                                    <span className="text-[#E53935]">Emergency</span>
                                    <span className="font-mono text-slate-900">24 × 7</span>
                                </div>
                            </div>
                        </InfoCard>

                        <a
                            href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("Hello VIL Engineering, I'd like to discuss a project.")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary justify-center w-full !py-4"
                            style={{ background: "#25D366", borderColor: "#25D366" }}
                            data-testid="contact-whatsapp-btn"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Chat on WhatsApp
                        </a>
                    </div>

                    <div>
                        <h2 className="font-display font-black text-slate-900 text-3xl md:text-4xl mb-3">
                            Send us your enquiry
                        </h2>
                        <p className="text-slate-600 mb-6 leading-relaxed">
                            Share details about your facility, scope of work, and timeline.
                            Our technical team will revert with a proposal.
                        </p>
                        <LeadForm />
                    </div>
                </div>
            </section>

            {/* Map */}
            <section className="pb-24" data-testid="map-section">
                <div className="container-vil">
                    <div className="corner-accent relative overflow-hidden border border-slate-200">
                        <iframe
                            title="VIL Engineering Office Location"
                            src="https://www.google.com/maps?q=Palghar+Maharashtra+401404&output=embed"
                            className="w-full h-[420px]"
                            style={{ filter: "invert(0.92) hue-rotate(180deg) saturate(0.5)" }}
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>
        </>
    );
};

const InfoCard = ({ icon, title, children }) => (
    <div className="glass p-6" data-testid={`info-card-${title.replace(/\s/g, "-").toLowerCase()}`}>
        <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E53935]/10 border border-[#E53935]/30">
                {icon}
            </div>
            <h3 className="font-display font-bold text-slate-900">{title}</h3>
        </div>
        {children}
    </div>
);

export default Contact;
